import { useState, type JSX } from 'react';
import { toast } from 'sonner';
import { Check, Loader2, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { LookalikeSound } from '../api/types';
import { useSuggestSound } from '../suggestions/hooks';
import { SoundLink, soundFallbackName } from './SoundLink';

/**
 * The sounds a lookalike analysis found several of the folder's creators
 * posting with: listen, open, and send one to Suggested searches to review
 * as a sound search. Nothing runs from here — the queue is where a sound
 * search gets its filters and topic words and is approved.
 */
export function LookalikeSounds({
  sounds,
  seedsUsed,
  folderPublicId,
  topicTerms,
}: {
  sounds: readonly LookalikeSound[];
  seedsUsed: number;
  folderPublicId: string;
  /** The folder's chosen (or suggested) hashtags, sent as the search's topic words. */
  topicTerms: string[];
}): JSX.Element | null {
  const send = useSuggestSound();
  const [sent, setSent] = useState<ReadonlySet<string>>(new Set());
  if (sounds.length === 0) return null;

  function submit(s: LookalikeSound) {
    const name = s.title ?? soundFallbackName(s.musicId);
    send.mutate(
      { platform: 'tiktok', musicId: s.musicId, title: s.title, topicTerms, folderPublicId, seedCount: s.seedCount, seedsUsed },
      {
        onSuccess: (r) => {
          setSent((prev) => new Set(prev).add(s.musicId));
          const topic =
            topicTerms.length > 0
              ? `Its topic words are this folder's tags (${topicTerms.slice(0, 4).join(', ')}${topicTerms.length > 4 ? '…' : ''}) — check them when you approve it.`
              : 'Approving it there needs topic words — add a few in the approve dialog, or every creator using the sound is kept.';
          if (r.created) toast.success(`Sent “${name}” to Suggested searches`, { description: topic });
          else {
            const status = r.suggestion.status;
            toast.info(`“${name}” is already in Suggested searches`, {
              description: status === 'pending' ? topic : `It was ${status} recently, so it isn't suggested again.`,
            });
          }
        },
        onError: (e) => toast.error(`Couldn't send “${name}”`, { description: (e as Error).message }),
      },
    );
  }

  return (
    <section className="mt-5 border-t border-border pt-4" aria-label="Shared sounds">
      <h4 className="text-sm font-semibold">Shared sounds</h4>
      <p className="mt-0.5 text-sm text-muted-foreground">
        Sounds at least two of these creators posted with. A sound search finds everyone using one — send it to
        Suggested searches to review and run it.
      </p>
      <ul className="mt-2 space-y-1">
        {sounds.map((s) => {
          const done = sent.has(s.musicId);
          const pending = send.isPending && send.variables?.musicId === s.musicId;
          return (
            <li key={s.musicId} className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-border px-2 py-1.5" data-lookalike-sound={s.musicId}>
              <SoundLink
                variant="row"
                platform="tiktok"
                id={s.musicId}
                title={s.title}
                author={s.author}
                coverUrl={s.coverUrl}
                playUrl={s.playUrl}
              />
              <span className="shrink-0 text-xs tabular-nums text-muted-foreground" title={`${s.seedCount} of the ${seedsUsed} creators read posted with it`}>
                used by {s.seedCount} of {seedsUsed} creators
              </span>
              <Button size="sm" variant="outline" disabled={done || pending} onClick={() => submit(s)}>
                {pending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : done ? (
                  <Check className="h-3.5 w-3.5" />
                ) : (
                  <Send className="h-3.5 w-3.5" />
                )}
                {done ? 'Sent' : 'Send to Suggested searches'}
              </Button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
